
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useBunnyVideoDetails } from '@/hooks/useBunnyVideoDetails';
import { Loader2, Search, Video, Clock, Eye, HardDrive, CheckCircle, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface BunnyVideoDetails {
  success: boolean;
  videoId: string;
  title: string;
  playUrl: string;
  thumbnailUrl: string | null;
  duration: number;
  status: number;
  encodeProgress: number;
  isPublic: boolean;
  resolution: {
    width: number;
    height: number;
  };
  uploadDate: string;
  views: number;
  storageSize: number;
}

interface BunnyVideoFetcherProps {
  onVideoSelect: (details: BunnyVideoDetails) => void;
  initialVideoId?: string;
  disabled?: boolean;
  autoFetch?: boolean;
}

export const BunnyVideoFetcher: React.FC<BunnyVideoFetcherProps> = ({
  onVideoSelect,
  initialVideoId = '',
  disabled = false,
  autoFetch = true
}) => {
  const [videoId, setVideoId] = useState(initialVideoId);
  const [videoDetails, setVideoDetails] = useState<BunnyVideoDetails | null>(null);
  
  const { fetchVideoDetails, isLoading, error } = useBunnyVideoDetails();

  const validateVideoId = (id: string): boolean => {
    // Bunny.net GUIDs: aceitar qualquer UUID no formato 8-4-4-4-12
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id.trim());
  };

  const handleFetchVideo = async () => {
    if (!videoId.trim()) {
      return;
    }

    if (!validateVideoId(videoId)) {
      return;
    }

    const details = await fetchVideoDetails(videoId.trim());
    if (details) {
      setVideoDetails(details);
      onVideoSelect(details);
    }
  };

  const handleInputChange = (value: string) => {
    setVideoId(value);
    // Clear video details when input changes
    if (videoDetails && value !== videoDetails.videoId) {
      setVideoDetails(null);
    }
  };

  // Busca automática ao colar/digitar um ID válido (debounce)
  const lastFetchedRef = useRef<string>('');
  useEffect(() => {
    if (!autoFetch || disabled) return;
    const trimmed = videoId.trim();
    if (!validateVideoId(trimmed) || lastFetchedRef.current === trimmed) return;

    const timer = setTimeout(async () => {
      lastFetchedRef.current = trimmed;
      const details = await fetchVideoDetails(trimmed);
      if (details) {
        setVideoDetails(details);
        onVideoSelect(details);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [videoId, autoFetch, disabled]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) {
      return `${mb.toFixed(1)} MB`;
    }
    return `${(mb / 1024).toFixed(1)} GB`;
  };

  const getStatusBadge = (status: number, encodeProgress: number) => {
    if (status === 4) {
      return (
        <Badge variant="default" className="bg-success text-success-foreground">
          <CheckCircle className="h-3 w-3 mr-1" />
          Pronto
        </Badge>
      );
    }
    if (status === 3) {
      return (
        <Badge variant="outline" className="border-accent/50 text-accent-foreground">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Processando ({encodeProgress}%)
        </Badge>
      );
    }
    if (status === 5) {
      return (
        <Badge variant="destructive">
          <AlertCircle className="h-3 w-3 mr-1" />
          Erro
        </Badge>
      );
    }
    return <Badge variant="secondary">Status {status}</Badge>;
  };

  const isValidId = videoId.trim() && validateVideoId(videoId);

  return (
    <div className="space-y-4">
      <div className="flex space-x-2">
        <div className="flex-1">
          <Label htmlFor="bunny-video-id" className="text-muted-foreground">
            ID do Vídeo Bunny.net *
          </Label>
          <Input
            id="bunny-video-id"
            value={videoId}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="Ex: 12345678-abcd-efgh-ijkl-123456789012"
            className={`bg-background/50 border-border text-foreground ${
              videoId && !isValidId ? 'border-destructive' : ''
            }`}
            disabled={disabled || isLoading}
          />
          {videoId && !isValidId && (
            <p className="text-destructive text-sm mt-1">
              ID deve estar no formato UUID (ex: 12345678-abcd-efgh-ijkl-123456789012)
            </p>
          )}
        </div>
        <div className="flex items-end">
          <Button
            onClick={handleFetchVideo}
            disabled={!isValidId || disabled || isLoading}
            variant="glow"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Buscando...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Buscar Vídeo
              </>
            )}
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-xl text-destructive text-sm">
          <AlertCircle className="h-4 w-4 inline mr-2" />
          {error}
        </div>
      )}

      {videoDetails && (
        <Card className="bg-card/70 backdrop-blur-md border-border/50 rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-start space-x-4">
              {videoDetails.thumbnailUrl && (
                <img
                  src={videoDetails.thumbnailUrl}
                  alt="Thumbnail do vídeo"
                  className="w-24 h-16 object-cover rounded border border-border"
                />
              )}
              
              <div className="flex-1 space-y-2">
                <div className="flex items-center space-x-2">
                  <Video className="h-4 w-4 text-primary" />
                  <h4 className="text-foreground font-medium truncate">{videoDetails.title}</h4>
                  {getStatusBadge(videoDetails.status, videoDetails.encodeProgress)}
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Clock className="h-3 w-3" />
                    <span>Duração: {formatDuration(videoDetails.duration)}</span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Eye className="h-3 w-3" />
                    <span>Visualizações: {videoDetails.views}</span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <HardDrive className="h-3 w-3" />
                    <span>Tamanho: {formatFileSize(videoDetails.storageSize)}</span>
                  </div>
                  
                  <div>
                    <span>Resolução: {videoDetails.resolution.width}x{videoDetails.resolution.height}</span>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground space-y-1">
                  <p><strong>Play URL:</strong> {videoDetails.playUrl}</p>
                  {videoDetails.thumbnailUrl && (
                    <p><strong>Thumbnail URL:</strong> {videoDetails.thumbnailUrl}</p>
                  )}
                  <p><strong>Público:</strong> {videoDetails.isPublic ? 'Sim' : 'Não'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
